// Workflow rules management. List + enable toggle + editor with a simple
// condition builder (all/any + field/op/value leafs) and action picker.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MenuPicker } from '@/components/ui/menu-picker';
import { useAccounts } from '@/lib/accounts';
import { useTags, useWorkspaceUsers } from '@/lib/collab';
import { useContactGroups } from '@/lib/contacts';
import { useTemplates } from '@/lib/templates';

// --- Types mirroring the API contract --------------------------------------

interface RuleLeaf {
  field: 'from' | 'to' | 'subject' | 'snippet' | 'text' | 'amount' | 'category';
  op: 'contains' | 'equals' | 'matches' | 'in_group' | 'lte' | 'gte' | 'eq';
  value: string;
  /** amount leaves only — undefined means "any currency". */
  currency?: 'USD' | 'GBP' | 'EUR';
  /** from leaves only — value is a natural-language sender description judged by AI. */
  ai?: boolean;
}

const STRING_FIELDS: ReadonlySet<RuleLeaf['field']> = new Set([
  'from',
  'to',
  'subject',
  'snippet',
  'text',
]);

// Keep in sync with the server's TRIAGE_CATEGORIES (services/ai.ts).
const CATEGORY_OPTIONS = [
  { value: 'important', label: 'Important' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'notification', label: 'Notification' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'promo', label: 'Promo' },
  { value: 'cold_outreach', label: 'Cold outreach' },
  { value: 'policy_update', label: 'Policy update' },
  { value: 'noise', label: 'Noise' },
] as const;

const AMOUNT_OPS = [
  { value: 'lte', label: 'is at most' },
  { value: 'gte', label: 'is at least' },
  { value: 'eq', label: 'is exactly' },
] as const;
type RuleConditions = { all: RuleLeaf[] } | { any: RuleLeaf[] };
type RuleAction =
  | { type: 'tag'; tagId: number }
  | { type: 'assign'; userId: number }
  | { type: 'archive' }
  | { type: 'star' }
  | { type: 'mark_read'; userId: number }
  | { type: 'summarise' }
  | { type: 'draft' }
  | { type: 'template_reply'; templateId: number };

interface Rule {
  id: number;
  name: string;
  enabled: boolean;
  priority: number;
  conditions: RuleConditions;
  actions: RuleAction[];
  accountId: number | null;
  createdAt: string;
}

// --- Hooks ------------------------------------------------------------------

function useRules() {
  return useQuery<{ rules: Rule[] }>({
    queryKey: ['rules'],
    queryFn: async () => {
      const r = await fetch('/api/rules', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

function useSaveRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: number;
      name: string;
      conditions: RuleConditions;
      actions: RuleAction[];
      accountId: number | null;
      enabled?: boolean;
    }) => {
      const url = input.id ? `/api/rules/${input.id}` : '/api/rules';
      const r = await fetch(url, {
        method: input.id ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string; detail?: string };
        // Server sends a human-readable `detail` for validation failures —
        // surface it instead of an opaque "bad_conditions".
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/rules/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

// --- Components ---------------------------------------------------------------

export function RulesSettings() {
  const rulesQ = useRules();
  const save = useSaveRule();
  const [editing, setEditing] = useState<Rule | 'new' | null>(null);

  if (rulesQ.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading rules…
        </CardContent>
      </Card>
    );
  }

  const rules = rulesQ.data?.rules ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Workflow rules
        </CardTitle>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing('new')}>
          <Plus className="size-3.5" strokeWidth={2} />
          New rule
        </Button>
      </CardHeader>
      <CardContent className="space-y-1 pt-0">
        <p className="pb-2 text-xs text-muted-foreground">
          Rules run on every new inbound email, in priority order. All matching rules apply.
        </p>
        {rules.length === 0 && !editing && (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-8 text-center">
            <Zap className="size-6 text-muted-foreground/50" strokeWidth={1.5} />
            <p className="text-xs text-muted-foreground">
              No rules yet. Try &ldquo;if subject contains invoice → tag Billing&rdquo;.
            </p>
          </div>
        )}
        {rules.map((r, i) => (
          <div key={r.id}>
            {i > 0 && <Separator />}
            <RuleRow
              rule={r}
              onEdit={() => setEditing(r)}
              onToggle={(enabled) =>
                save.mutate(
                  { id: r.id, name: r.name, conditions: r.conditions, actions: r.actions, accountId: r.accountId, enabled },
                  { onError: (e) => toast.error(e.message) },
                )
              }
            />
          </div>
        ))}
        {editing && (
          <RuleEditor
            initial={editing === 'new' ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function describeConditions(c: RuleConditions, groupName?: (id: number) => string): string {
  const list = 'all' in c ? c.all : c.any;
  const joiner = 'all' in c ? ' AND ' : ' OR ';
  return list
    .map((l) => {
      if (l.field === 'from' && l.ai) return `sender matches AI: "${l.value}"`;
      if (l.op === 'in_group') {
        return `from is in group "${groupName?.(Number(l.value)) ?? `#${l.value}`}"`;
      }
      if (l.field === 'amount') {
        const op = l.op === 'lte' ? '≤' : l.op === 'gte' ? '≥' : '=';
        return `amount ${op} ${l.value}${l.currency ? ` ${l.currency}` : ''}`;
      }
      if (l.field === 'category') {
        const cat = CATEGORY_OPTIONS.find((o) => o.value === l.value)?.label ?? l.value;
        return `AI category is ${cat}`;
      }
      if (l.field === 'text') return `subject or body ${l.op} "${l.value}"`;
      return `${l.field} ${l.op} "${l.value}"`;
    })
    .join(joiner);
}

/** Dropdown of the workspace's contact groups — the value for a `from in_group`
 *  condition. */
function GroupValuePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const groupsQ = useContactGroups();
  const groups = groupsQ.data?.groups ?? [];
  // Seed the leaf with the first group id so "is in group" is valid without a
  // manual pick (empty values get dropped on save).
  useEffect(() => {
    if (!value && groups.length > 0) onChange(String(groups[0]!.id));
  }, [value, groups, onChange]);
  if (groups.length === 0) {
    return (
      <span className="min-w-[120px] flex-1 rounded-md border border-dashed px-2 py-1 text-xs text-muted-foreground">
        No groups yet — create one in Contacts
      </span>
    );
  }
  return (
    <MenuPicker
      variant="inline"
      className="min-w-[120px] flex-1 border"
      value={value || String(groups[0]!.id)}
      onChange={onChange}
      options={groups.map((g) => ({ value: String(g.id), label: g.name }))}
    />
  );
}

function RuleRow({
  rule,
  onEdit,
  onToggle,
}: {
  rule: Rule;
  onEdit: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const del = useDeleteRule();
  const tagsQ = useTags();
  const usersQ = useWorkspaceUsers();
  const templatesQ = useTemplates();
  const groupsQ = useContactGroups();
  const groupName = (id: number) => groupsQ.data?.groups.find((g) => g.id === id)?.name ?? `#${id}`;

  function describeAction(a: RuleAction): string {
    switch (a.type) {
      case 'tag': {
        const t = tagsQ.data?.tags.find((x) => x.id === a.tagId);
        return `tag ${t?.name ?? `#${a.tagId}`}`;
      }
      case 'assign': {
        const u = usersQ.data?.users.find((x) => x.id === a.userId);
        return `assign ${u?.name || u?.email || `user ${a.userId}`}`;
      }
      case 'archive':
        return 'archive (mark read)';
      case 'star':
        return 'star';
      case 'mark_read':
        return 'mark read';
      case 'summarise':
        return 'AI-summarise';
      case 'draft':
        return 'draft AI reply';
      case 'template_reply': {
        const t = templatesQ.data?.templates.find((x) => x.id === a.templateId);
        return `pre-fill reply "${t?.name ?? `template #${a.templateId}`}"`;
      }
      default:
        return 'unknown';
    }
  }

  return (
    <div className="flex items-start gap-3 py-3">
      <input
        type="checkbox"
        checked={rule.enabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="mt-1 size-4 shrink-0 cursor-pointer accent-primary"
        title={rule.enabled ? 'Disable rule' : 'Enable rule'}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`truncate text-sm font-medium ${rule.enabled ? '' : 'text-muted-foreground line-through'}`}>
            {rule.name}
          </span>
          {!rule.enabled && (
            <Badge variant="outline" className="h-4 px-1.5 text-[9px]">
              off
            </Badge>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          If {describeConditions(rule.conditions, groupName)} → {rule.actions.map(describeAction).join(', ')}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button variant="ghost" size="icon" className="size-7" onClick={onEdit} aria-label="Edit rule">
          <Pencil className="size-3.5" strokeWidth={1.75} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground hover:text-destructive"
          onClick={() => {
            if (confirm(`Delete rule "${rule.name}"?`)) del.mutate(rule.id);
          }}
          disabled={del.isPending}
          aria-label="Delete rule"
        >
          <Trash2 className="size-3.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}

function RuleEditor({ initial, onClose }: { initial: Rule | null; onClose: () => void }) {
  const save = useSaveRule();
  const tagsQ = useTags();
  const usersQ = useWorkspaceUsers();
  const accountsQ = useAccounts();
  const templatesQ = useTemplates();

  const [name, setName] = useState(initial?.name ?? '');
  const [mode, setMode] = useState<'all' | 'any'>(initial && 'any' in initial.conditions ? 'any' : 'all');
  const [leafs, setLeafs] = useState<RuleLeaf[]>(
    initial ? ('all' in initial.conditions ? initial.conditions.all : initial.conditions.any) : [{ field: 'from', op: 'contains', value: '' }],
  );
  const [actions, setActions] = useState<RuleAction[]>(initial?.actions ?? []);
  const [accountId, setAccountId] = useState<number | null>(initial?.accountId ?? null);

  function updateLeaf(i: number, patch: Partial<RuleLeaf>) {
    setLeafs((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  }

  /** Wholesale swap — used when changing field/op kind so stale keys
   *  (currency, ai) can't leak into a leaf they don't belong to. */
  function replaceLeaf(i: number, leaf: RuleLeaf) {
    setLeafs((prev) => prev.map((l, j) => (j === i ? leaf : l)));
  }

  function submit() {
    const cleanLeafs = leafs.filter((l) => l.value.trim());
    if (!name.trim() || cleanLeafs.length === 0 || actions.length === 0) {
      toast.error('Rule needs a name, at least one condition with a value, and one action');
      return;
    }
    const badAmount = cleanLeafs.find(
      (l) => l.field === 'amount' && !Number.isFinite(Number(l.value)),
    );
    if (badAmount) {
      toast.error('Amount must be a plain number — no currency symbol');
      return;
    }
    save.mutate(
      {
        id: initial?.id,
        name: name.trim(),
        conditions: mode === 'all' ? { all: cleanLeafs } : { any: cleanLeafs },
        actions,
        accountId,
      },
      {
        onSuccess: () => {
          toast.success(initial ? 'Rule updated' : 'Rule created');
          onClose();
        },
        onError: (e) => toast.error(e.message),
      },
    );
  }

  const tags = tagsQ.data?.tags ?? [];
  const users = usersQ.data?.users ?? [];
  const accounts = accountsQ.data?.accounts ?? [];
  const templates = templatesQ.data?.templates ?? [];

  return (
    <div className="mt-3 space-y-4 rounded-md border bg-card/50 p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{initial ? 'Edit rule' : 'New rule'}</span>
        <Button variant="ghost" size="xs" onClick={onClose}>
          Cancel
        </Button>
      </div>

      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Rule name — e.g. Tag invoices"
        maxLength={100}
        className="w-full rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
      />

      {/* Scope */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Applies to</span>
        <MenuPicker
          value={accountId == null ? 'all' : String(accountId)}
          onChange={(v) => setAccountId(v === 'all' ? null : Number(v))}
          options={[
            { value: 'all', label: 'All inboxes' },
            ...accounts.map((a) => ({ value: String(a.id), label: a.emailAddress })),
          ]}
        />
      </div>

      {/* Conditions */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold">If</span>
          <MenuPicker
            value={mode}
            onChange={(v) => setMode(v as 'all' | 'any')}
            options={[
              { value: 'all', label: 'all conditions match' },
              { value: 'any', label: 'any condition matches' },
            ]}
          />
        </div>
        {leafs.map((l, i) => (
          <div key={i} className="flex flex-wrap items-center gap-1.5">
            <MenuPicker
              variant="inline"
              className="border"
              value={l.field}
              onChange={(v) => {
                const field = v as RuleLeaf['field'];
                if (STRING_FIELDS.has(field) && STRING_FIELDS.has(l.field)) {
                  // Text-ish fields keep the typed value; ops that only exist
                  // on 'from' (in group / AI description) reset when leaving.
                  if (field !== 'from' && (l.op === 'in_group' || l.ai)) {
                    replaceLeaf(i, { field, op: 'contains', value: '' });
                  } else {
                    updateLeaf(i, { field });
                  }
                } else if (field === 'amount') {
                  replaceLeaf(i, { field, op: 'lte', value: '' });
                } else if (field === 'category') {
                  replaceLeaf(i, { field, op: 'equals', value: 'notification' });
                } else {
                  replaceLeaf(i, { field, op: 'contains', value: '' });
                }
              }}
              options={[
                { value: 'from', label: 'From' },
                { value: 'to', label: 'To' },
                { value: 'subject', label: 'Subject' },
                { value: 'snippet', label: 'Body preview' },
                { value: 'text', label: 'Subject + body' },
                { value: 'amount', label: 'Amount' },
                { value: 'category', label: 'AI category' },
              ]}
            />
            {l.field === 'category' ? (
              <span className="px-1 text-xs text-muted-foreground">is</span>
            ) : (
              <MenuPicker
                variant="inline"
                className="border"
                value={l.field !== 'amount' && l.ai ? 'ai' : l.op}
                onChange={(v) => {
                  if (l.field === 'amount') {
                    updateLeaf(i, { op: v as RuleLeaf['op'] });
                    return;
                  }
                  if (v === 'ai') {
                    replaceLeaf(i, { field: 'from', op: 'contains', value: '', ai: true });
                    return;
                  }
                  const op = v as RuleLeaf['op'];
                  // in_group swaps the value between a group id and free text,
                  // and leaving the AI mode drops the description — clear the
                  // value on any of those transitions.
                  if (op === 'in_group' || l.op === 'in_group' || l.ai) {
                    replaceLeaf(i, { field: l.field, op, value: '' });
                  } else {
                    updateLeaf(i, { op });
                  }
                }}
                options={
                  l.field === 'amount'
                    ? [...AMOUNT_OPS]
                    : [
                        { value: 'contains', label: 'contains' },
                        { value: 'equals', label: 'equals' },
                        { value: 'matches', label: 'matches regex' },
                        ...(l.field === 'from'
                          ? [
                              { value: 'in_group', label: 'is in group' },
                              { value: 'ai', label: 'matches AI description' },
                            ]
                          : []),
                      ]
                }
              />
            )}
            {l.field === 'category' ? (
              <MenuPicker
                variant="inline"
                className="min-w-[140px] border"
                value={l.value}
                onChange={(v) => updateLeaf(i, { value: v })}
                options={[...CATEGORY_OPTIONS]}
              />
            ) : l.field === 'amount' ? (
              <>
                <input
                  type="text"
                  inputMode="decimal"
                  value={l.value}
                  onChange={(e) => updateLeaf(i, { value: e.target.value.replace(/[^0-9.]/g, '') })}
                  placeholder="20"
                  maxLength={12}
                  className="w-24 rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
                />
                <MenuPicker
                  variant="inline"
                  className="border"
                  value={l.currency ?? 'any'}
                  onChange={(v) =>
                    updateLeaf(i, {
                      currency: v === 'any' ? undefined : (v as RuleLeaf['currency']),
                    })
                  }
                  options={[
                    { value: 'any', label: 'any currency' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'USD', label: 'USD' },
                    { value: 'EUR', label: 'EUR' },
                  ]}
                />
              </>
            ) : l.op === 'in_group' ? (
              <GroupValuePicker value={l.value} onChange={(v) => updateLeaf(i, { value: v })} />
            ) : (
              <input
                type="text"
                value={l.value}
                onChange={(e) => updateLeaf(i, { value: e.target.value })}
                placeholder={l.ai ? 'describe the sender — e.g. any payment provider' : 'value'}
                maxLength={200}
                className="min-w-[120px] flex-1 rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
              />
            )}
            {leafs.length > 1 && (
              <button
                type="button"
                onClick={() => setLeafs((prev) => prev.filter((_, j) => j !== i))}
                className="rounded p-1 text-muted-foreground hover:text-destructive"
                aria-label="Remove condition"
              >
                <Trash2 className="size-3" strokeWidth={2} />
              </button>
            )}
          </div>
        ))}
        {leafs.length < 10 && (
          <Button
            variant="ghost"
            size="xs"
            className="gap-1"
            onClick={() => setLeafs((prev) => [...prev, { field: 'from', op: 'contains', value: '' }])}
          >
            <Plus className="size-3" strokeWidth={2} />
            Add condition
          </Button>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <div className="text-xs font-semibold">Then</div>
        {actions.map((a, i) => (
          <div key={i} className="flex flex-wrap items-center gap-1.5">
            <MenuPicker
              variant="inline"
              className="border"
              value={a.type}
              onChange={(v) => {
                const type = v as RuleAction['type'];
                setActions((prev) =>
                  prev.map((x, j) =>
                    j === i
                      ? type === 'tag'
                        ? { type, tagId: tags[0]?.id ?? 0 }
                        : type === 'assign' || type === 'mark_read'
                          ? { type, userId: users[0]?.id ?? 0 }
                          : type === 'template_reply'
                            ? { type, templateId: templates[0]?.id ?? 0 }
                            : { type }
                      : x,
                  ) as RuleAction[],
                );
              }}
              options={[
                { value: 'tag', label: 'Add tag' },
                { value: 'assign', label: 'Assign to' },
                { value: 'archive', label: 'Archive (mark read)' },
                { value: 'star', label: 'Star' },
                { value: 'mark_read', label: 'Mark read for' },
                { value: 'summarise', label: 'Always AI-summarise' },
                { value: 'draft', label: 'Draft AI response' },
                { value: 'template_reply', label: 'Pre-fill reply from template' },
              ]}
            />
            {a.type === 'template_reply' && (
              <MenuPicker
                variant="inline"
                className="min-w-[160px] border"
                value={String(a.templateId)}
                onChange={(v) =>
                  setActions((prev) =>
                    prev.map((x, j) =>
                      j === i ? ({ type: 'template_reply', templateId: Number(v) } as RuleAction) : x,
                    ),
                  )
                }
                options={
                  templates.length > 0
                    ? templates.map((t) => ({ value: String(t.id), label: t.name }))
                    : [{ value: '0', label: 'No templates yet — add one in Settings' }]
                }
              />
            )}
            {a.type === 'tag' && (
              <MenuPicker
                variant="inline"
                className="border"
                value={String(a.tagId)}
                onChange={(v) =>
                  setActions((prev) =>
                    prev.map((x, j) => (j === i ? { type: 'tag', tagId: Number(v) } : x)) as RuleAction[],
                  )
                }
                options={tags.map((t) => ({ value: String(t.id), label: t.name }))}
              />
            )}
            {(a.type === 'assign' || a.type === 'mark_read') && (
              <MenuPicker
                variant="inline"
                className="border"
                value={String(a.userId)}
                onChange={(v) =>
                  setActions((prev) =>
                    prev.map((x, j) =>
                      j === i ? ({ type: a.type, userId: Number(v) } as RuleAction) : x,
                    ),
                  )
                }
                options={users.map((u) => ({ value: String(u.id), label: u.name || u.email }))}
              />
            )}
            <button
              type="button"
              onClick={() => setActions((prev) => prev.filter((_, j) => j !== i))}
              className="rounded p-1 text-muted-foreground hover:text-destructive"
              aria-label="Remove action"
            >
              <Trash2 className="size-3" strokeWidth={2} />
            </button>
          </div>
        ))}
        {actions.length < 10 && (
          <Button
            variant="ghost"
            size="xs"
            className="gap-1"
            onClick={() => setActions((prev) => [...prev, { type: 'archive' }])}
          >
            <Plus className="size-3" strokeWidth={2} />
            Add action
          </Button>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={save.isPending} className="gap-1.5">
          {save.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Zap className="size-3.5" strokeWidth={2} />}
          {initial ? 'Save changes' : 'Create rule'}
        </Button>
      </div>
    </div>
  );
}
